import React, { useMemo } from "react";

/**
 * Module-grouped checkbox list against /api/v1/admin/permissions.
 * Shared by Roles, Designations, and the User permission-override screen -
 * the one non-trivial UI pattern that repeats across all three (handoff §10.2).
 */
const PermissionChecklist = ({
  permissions = [],
  selectedIds = [],
  onToggle,
  disabled = false,
}) => {
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const grouped = useMemo(() => {
    const map = new Map();
    permissions.forEach((perm) => {
      if (!map.has(perm.moduleCode)) {
        map.set(perm.moduleCode, { moduleName: perm.moduleName, items: [] });
      }
      map.get(perm.moduleCode).items.push(perm);
    });
    return Array.from(map.values());
  }, [permissions]);

  if (grouped.length === 0) {
    return <p className="text-muted mb-0">No permissions available.</p>;
  }

  // A "ViewAll" permission only *widens scope* on top of the base "View"
  // (own-only -> everyone's) - it was never meant to be grantable on its
  // own, but nothing stopped an admin from ticking ViewAll without View,
  // producing a role that gets a clean 403 on every List/Detail call
  // despite "having ViewAll". Hit for real twice (Collections' testeradmin,
  // PettyCash's FINANCE_MANAGER) before this got fixed here. Bundling the
  // two checkboxes removes the invalid combination at the source instead of
  // relying on admins remembering the pairing.
  const handleToggle = (group, perm, checked) => {
    onToggle(perm.permissionId, checked);

    if (checked && perm.permissionCode.endsWith(".ViewAll")) {
      const baseCode = perm.permissionCode.replace(/ViewAll$/, "View");
      const viewPerm = group.items.find((p) => p.permissionCode === baseCode);
      if (viewPerm && !selected.has(viewPerm.permissionId)) {
        onToggle(viewPerm.permissionId, true);
      }
    }

    if (!checked && perm.permissionCode.endsWith(".View")) {
      const viewAllCode = perm.permissionCode.replace(/View$/, "ViewAll");
      const viewAllPerm = group.items.find((p) => p.permissionCode === viewAllCode);
      if (viewAllPerm && selected.has(viewAllPerm.permissionId)) {
        onToggle(viewAllPerm.permissionId, false);
      }
    }
  };

  return (
    <div className="permission-checklist">
      {grouped.map((group) => (
        <div className="mb-3" key={group.moduleName}>
          <h6 className="mb-2">{group.moduleName}</h6>
          <div className="row">
            {group.items.map((perm) => (
              <div className="col-md-4 col-sm-6 mb-2" key={perm.permissionId}>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`perm-${perm.permissionId}`}
                    checked={selected.has(perm.permissionId)}
                    disabled={disabled}
                    onChange={(e) =>
                      handleToggle(group, perm, e.target.checked)
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`perm-${perm.permissionId}`}
                    title={perm.description}
                  >
                    {/* Module/page name is already the section header above -
                        showing the full code here would be redundant
                        (handoff §13.3), e.g. "Products.Edit" -> just "Edit". */}
                    {perm.permissionCode.split(".").slice(1).join(".") || perm.permissionCode}
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PermissionChecklist;
